import type { ProofStandard } from "@/hooks/useProofStandards";
import { getProofStandardRooms } from "@/lib/unitCheckProofKeys";

export type UnitCheckRoomSection = { label: string; items: ProofStandard[] };

function sortByDisplayOrder(a: ProofStandard, b: ProofStandard): number {
  return a.display_order - b.display_order || a.created_at.localeCompare(b.created_at);
}

/** Sort by display_order, then group consecutive rows with the same room label. */
export function groupProofStandardsByRoomPreservingOrder(
  standards: ProofStandard[],
  generalLabel = "General",
): UnitCheckRoomSection[] {
  const sorted = [...standards].sort(sortByDisplayOrder);
  const sections: UnitCheckRoomSection[] = [];
  for (const s of sorted) {
    const label = s.room?.trim() || generalLabel;
    const last = sections[sections.length - 1];
    if (last && last.label === label) last.items.push(s);
    else sections.push({ label, items: [s] });
  }
  return sections;
}

export function sortProofStandardsByDisplayOrder(standards: ProofStandard[]): ProofStandard[] {
  return [...standards].sort(sortByDisplayOrder);
}

/**
 * Room-by-room walk order for unit checks: follows checklist room order (door-first),
 * then any extra room labels, then General for unassigned items.
 */
export function buildUnitCheckWalkthrough(
  standards: ProofStandard[],
  checklistRoomNames: string[],
  generalLabel = "General",
): UnitCheckRoomSection[] {
  if (standards.length === 0) return [];

  const byRoom = new Map<string, ProofStandard[]>();

  for (const name of checklistRoomNames) {
    const key = name.trim();
    if (key) byRoom.set(key, []);
  }
  if (!byRoom.has(generalLabel)) byRoom.set(generalLabel, []);

  const sorted = [...standards].sort(sortByDisplayOrder);
  for (const s of sorted) {
    const rooms = getProofStandardRooms(s, checklistRoomNames, generalLabel);
    for (const roomName of rooms) {
      if (!byRoom.has(roomName)) byRoom.set(roomName, []);
      byRoom.get(roomName)!.push(s);
    }
  }

  for (const items of byRoom.values()) {
    items.sort(sortByDisplayOrder);
  }

  const sections: UnitCheckRoomSection[] = [];

  for (const name of checklistRoomNames) {
    const key = name.trim();
    if (!key) continue;
    const items = byRoom.get(key) ?? [];
    if (items.length > 0) sections.push({ label: key, items });
    byRoom.delete(key);
  }

  const extraRooms = [...byRoom.entries()]
    .filter(([label]) => label !== generalLabel)
    .sort(([a], [b]) => a.localeCompare(b));

  for (const [label, items] of extraRooms) {
    if (items.length > 0) sections.push({ label, items });
  }

  const generalItems = byRoom.get(generalLabel) ?? [];
  if (generalItems.length > 0) {
    sections.push({ label: generalLabel, items: generalItems });
  }

  return sections;
}

export function flattenUnitCheckWalkthrough(sections: UnitCheckRoomSection[]): ProofStandard[] {
  return sections.flatMap((s) => s.items);
}

/** Assign display_order from walkthrough (first appearance per standard; preserves multi-room assignments). */
export function reindexProofStandardsFromWalkthrough(
  sections: UnitCheckRoomSection[],
): Array<{ id: string; display_order: number }> {
  const updates: Array<{ id: string; display_order: number }> = [];
  const seen = new Set<string>();
  let order = 0;
  for (const section of sections) {
    for (const item of section.items) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      updates.push({ id: item.id, display_order: order++ });
    }
  }
  return updates;
}

export type UnitCheckWalkStep =
  | {
      kind: "task";
      roomName: string;
      standard: ProofStandard;
      roomIndex: number;
      roomCount: number;
      taskIndex: number;
      taskCount: number;
      globalIndex: number;
    }
  | {
      kind: "room_transition";
      nextRoomName: string;
      roomIndex: number;
      roomCount: number;
      globalIndex: number;
    };

export function buildUnitCheckWalkSteps(sections: UnitCheckRoomSection[]): UnitCheckWalkStep[] {
  const steps: UnitCheckWalkStep[] = [];
  let globalIndex = 0;
  const roomCount = sections.length;

  sections.forEach((section, roomIndex) => {
    const taskCount = section.items.length;
    section.items.forEach((standard, taskIndex) => {
      steps.push({
        kind: "task",
        roomName: section.label,
        standard,
        roomIndex,
        roomCount,
        taskIndex,
        taskCount,
        globalIndex: globalIndex++,
      });
    });
    if (roomIndex < sections.length - 1 && taskCount > 0) {
      steps.push({
        kind: "room_transition",
        nextRoomName: sections[roomIndex + 1]!.label,
        roomIndex,
        roomCount,
        globalIndex: globalIndex++,
      });
    }
  });

  return steps;
}
